import { fmt } from '@dcc-erp/shared';

import { getCat } from './pp-helpers';

import type { CatCode, PPCat, PPItem } from './pp-helpers';

interface CatSummary extends PPCat {
  count: number;
  monthly: number;
  remain: number;
  original: number;
}

const FORECAST_MONTHS = [
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
] as const;

interface SummaryProps {
  items: readonly PPItem[];
}

export function PPSummary({ items }: SummaryProps) {
  const byCat = new Map<CatCode, CatSummary>();
  items
    .filter((r) => r.status === 'active')
    .forEach((r) => {
      const existing = byCat.get(r.cat);
      if (existing) {
        existing.count++;
        existing.monthly += r.monthly;
        existing.remain += r.monthly * r.remain;
        existing.original += r.total;
      } else {
        byCat.set(r.cat, {
          ...getCat(r.cat),
          count: 1,
          monthly: r.monthly,
          remain: r.monthly * r.remain,
          original: r.total,
        });
      }
    });
  const list = Array.from(byCat.values()).sort((a, b) => b.remain - a.remain);
  const totalRemain = list.reduce((s, c) => s + c.remain, 0);

  const forecast = FORECAST_MONTHS.map((_, i) =>
    items.filter((r) => r.status === 'active' && r.remain > i).reduce((s, r) => s + r.monthly, 0),
  );
  const maxForecast = Math.max(...forecast, 1);

  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-head">
          <h3>ค่าใช้จ่ายจ่ายล่วงหน้าตามหมวด</h3>
          <span className="meta">รวมคงเหลือ {fmt(totalRemain)} บาท</span>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>หมวด</th>
                <th className="num">สัญญา</th>
                <th className="num">ตัดจ่าย/เดือน</th>
                <th className="num">คงเหลือ</th>
                <th style={{ width: 200 }}>สัดส่วน</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.code}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{ width: 10, height: 10, borderRadius: 2, background: c.tone }}
                      />
                      <span>
                        <b>{c.label}</b>
                        <div className="meta" style={{ marginTop: 1 }}>
                          {c.acct}
                        </div>
                      </span>
                    </span>
                  </td>
                  <td className="num">{c.count}</td>
                  <td className="num tnum">{fmt(c.monthly)}</td>
                  <td className="num tnum">
                    <b>{fmt(c.remain)}</b>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          background: '#f0f0f3',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${totalRemain ? (c.remain / totalRemain) * 100 : 0}%`,
                            height: '100%',
                            background: c.tone,
                          }}
                        />
                      </div>
                      <span className="meta" style={{ minWidth: 40, textAlign: 'right' }}>
                        {totalRemain ? ((c.remain / totalRemain) * 100).toFixed(1) : '0.0'}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
              <tr className="total">
                <td>รวมทั้งสิ้น</td>
                <td className="num">{list.reduce((s, c) => s + c.count, 0)}</td>
                <td className="num tnum">{fmt(list.reduce((s, c) => s + c.monthly, 0))}</td>
                <td className="num tnum">{fmt(totalRemain)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>ตัดจ่ายอัตโนมัติ — Forecast 12 เดือนข้างหน้า</h3>
          <span className="meta">ตามตารางที่ลงทะเบียนไว้แล้ว</span>
        </div>
        <div className="card-body">
          <div className="bars">
            {FORECAST_MONTHS.map((m, i) => (
              <div className="bcol" key={m}>
                <div className="bstack">
                  <div
                    className="b1"
                    style={{ height: `${((forecast[i] ?? 0) / maxForecast) * 100}%` }}
                  />
                </div>
                <div className="blabel">{m}</div>
              </div>
            ))}
          </div>
          <div className="divider" />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 14,
              fontSize: 14,
            }}
          >
            <div>
              <div className="meta">เดือนสูงสุด</div>
              <div style={{ fontWeight: 600, fontSize: 16, marginTop: 2 }} className="tnum">
                {fmt(Math.max(...forecast))}
              </div>
            </div>
            <div>
              <div className="meta">เดือนต่ำสุด (เดือนที่ 12)</div>
              <div style={{ fontWeight: 600, fontSize: 16, marginTop: 2 }} className="tnum">
                {fmt(Math.min(...forecast))}
              </div>
            </div>
            <div>
              <div className="meta">รวม 12 เดือน</div>
              <div style={{ fontWeight: 600, fontSize: 16, marginTop: 2 }} className="tnum">
                {fmt(forecast.reduce((a, b) => a + b, 0))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
